import React, { useEffect, useState } from "react";
import Chat from "../../common/Chat/Chat";
import { NotesStyles } from "./NotesStyles";

import { Box, styled, TextField, Typography } from "@mui/material";
import CustomButton from "../../common/CustomButton/CustomButton";
import {
  CLIENT_NAME,
  CONTACT,
  DUE_DATE,
  PERSON_LOGO,
  POLICY,
  SAVE,
  ADD_NOTES,
} from "../../constants";
import { PostAddNotes } from "./NotesActions";
import { useAppDispatch } from "../../store/hooks";
import { getCookie } from "../../utils/Utility";
import { useParams } from "react-router-dom";
import { CustomerData } from "../CustomerDetailsList/customerDetailsListType";

interface PersonDataProps {
  personLogo: string;
  clientName: string;
  mobileNumber: string;
  policyNumber: string;
  dueDate: string;
}

interface UserDetailsProps {
  personLogo: string;
  clientName: string;
  dateAndTime: string;
  userMessage: boolean;
}

interface PersonChatProps {
  message: string;
  userDetails: UserDetailsProps;
}

interface PersonNotesProps {
  personDataArray: PersonDataProps;
  personChatArray: PersonChatProps[];
  handleNotesClick?: () => void;
  setOpenSnackbar?: (value: boolean) => void;
  customerDetails?: CustomerData;
}

const StyledTextField = styled(TextField)({
  width: "100%",
  cursor: "text",
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      border: "1px solid var(--primary-grey)",
    },
    "&.Mui-focused fieldset": {
      border: "1px solid var(--primary-grey)",
    },
  },
  "& .MuiInputBase-root": {
    // Remove internal scrollbar from TextField
    "& textarea": {
      overflow: "hidden",
    },
  },
});

const getFormattedDateTime = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const Notes: React.FC<PersonNotesProps> = ({
  personDataArray,
  personChatArray,
  handleNotesClick,
  setOpenSnackbar,
  customerDetails,
}) => {
  const { customerId } = useParams();
  const [savedNotes, setSavedNotes] = useState<PersonChatProps[]>([]);
  const providerId = getCookie("id");
  const dispatch = useAppDispatch();
  const [notes, setNotes] = useState("");
  const [textareaRows, setTextareaRows] = useState(3);

  const calculateRows = (text: string) => {
    const lineCount = (text.match(/\n/g) || []).length + 1;
    return Math.max(3, Math.min(18, lineCount)); // Min 3 rows, max 8 rows
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setNotes(newValue);
    setTextareaRows(calculateRows(newValue));
  };

  const handler = () => {
    if (!notes.trim()) return;
    if (customerId && providerId) {
      const requestBody = {
        providerId: providerId,
        customerId: customerId,
        createdTime: getFormattedDateTime(),
        note: notes,
      };

      dispatch(PostAddNotes(requestBody));
    }
    setOpenSnackbar?.(true);
    handleNotesClick?.();
  };

  return (
    <Box
      id="notes-container"
      data-test-id="test-notes-container"
      aria-label="Notes container"
      sx={{
        ...NotesStyles.notesContainer,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Top section with client details */}
      <Box
        id="notes-client-details-section"
        data-test-id="test-notes-client-details-section"
        aria-label="Client details section"
        sx={{
          pt: 3,
          pb: 2,
          flexShrink: 0,
        }}
      >
        <Box 
          id="notes-client-details-container"
          data-test-id="test-notes-client-details-container"
          aria-label="Client details container"
          sx={NotesStyles.clientDetailsContainer}
        >
          <img 
            id="notes-client-avatar"
            data-test-id="test-notes-client-avatar"
            aria-label="Client avatar"
            src={personDataArray.personLogo} 
            alt={PERSON_LOGO} 
          />
          <Box 
            id="notes-client-info"
            data-test-id="test-notes-client-info"
            aria-label="Client information"
            sx={NotesStyles.clientContainer}
          >
            <Typography 
              id="notes-client-name"
              data-test-id="test-notes-client-name"
              aria-label="Client name"
              sx={NotesStyles.clientName}
            >
              {CLIENT_NAME}: {customerDetails?.customerName}{" "}
              {customerDetails?.customerSurName}
            </Typography>
            <Box 
              id="notes-client-contact"
              data-test-id="test-notes-client-contact"
              aria-label="Client contact information"
              sx={NotesStyles.clientDetails}
            >
              <Typography 
                id="notes-client-phone"
                data-test-id="test-notes-client-phone"
                aria-label="Client phone number"
                sx={NotesStyles.personDetails}
              >
                {CONTACT} : {customerDetails?.phoneInfo}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Middle section - flexible space */}
      <Box
        id="notes-middle-section"
        data-test-id="test-notes-middle-section"
        aria-label="Notes middle section"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: "10px",
          mb: 2,
        }}
      >
        {/* This section can be empty or have minimal content */}
      </Box>

      {/* Bottom section with notes input and buttons */}
      <Box
        id="notes-input-section"
        data-test-id="test-notes-input-section"
        aria-label="Notes input section"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          pb: 4,
          flexShrink: 0,
        }}
      >
        <StyledTextField
          id="notes-text-input"
          data-test-id="test-notes-text-input"
          aria-label="Notes text input"
          placeholder={ADD_NOTES}
          value={notes}
          onChange={handleNotesChange}
          multiline
          minRows={textareaRows}
          maxRows={textareaRows} // Setting equal to minRows to prevent scrolling
          autoFocus
        />
        <Box
          id="notes-buttons-container"
          data-test-id="test-notes-buttons-container"
          aria-label="Notes action buttons"
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            mt: 1,
          }}
        >
          <CustomButton onClick={handleNotesClick} text="Cancel" />
          <CustomButton onClick={handler} text={SAVE} />
        </Box>
      </Box>
    </Box>
  );
};

export default Notes;
